import { useState, type ReactNode } from 'react';
import ChartFullscreen from './charts/ChartFullscreen';
import './Surveys.css';

// Generic wrapper used by every chart on the surveys page so the section
// header / subtitle / legend / SVG live inside one bordered card with
// consistent padding. Per the spec, the h2 and subhead live INSIDE the
// card (the inspection HTML put them outside; we're folding them in).
//
// `expandable` (default true) adds a top-right "Expand" button that
// renders the same title/subtitle/legend/children inside a portal-mounted
// fullscreen modal.

interface Props {
  title?: string;
  subtitle?: string;
  legend?: ReactNode;
  expandable?: boolean;
  children: ReactNode;
}

export default function ChartCard({
  title,
  subtitle,
  legend,
  expandable = true,
  children,
}: Props) {
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <>
      <section className="chart-card-section">
        {expandable && (
          <button
            type="button"
            className="expand-btn"
            onClick={() => setFullscreen(true)}
            aria-label="Expand chart to full screen"
            title="Expand to full screen"
          >
            <span className="expand-btn__icon" aria-hidden="true">
              ⤢
            </span>
            <span>Expand</span>
          </button>
        )}
        {title && <h2 className="chart-card-section__title">{title}</h2>}
        {subtitle && <p className="chart-card-section__subtitle">{subtitle}</p>}
        {legend}
        {children}
      </section>

      {fullscreen && (
        <ChartFullscreen
          title={title}
          subtitle={subtitle}
          legend={legend}
          onClose={() => setFullscreen(false)}
        >
          {children}
        </ChartFullscreen>
      )}
    </>
  );
}
